import { useEffect, useRef } from 'react';
import Editor from 'react-simple-code-editor';
import Prism from 'prismjs';
import 'prismjs/components/prism-yaml';
import 'prismjs/themes/prism-tomorrow.css';
import yaml from 'js-yaml';

interface WorkflowConfigEditorProps {
  value: string;
  onChange: (yaml: string, isValid: boolean) => void;
  placeholder?: string;
  isLoading?: boolean;
}

export function WorkflowConfigEditor({
  value,
  onChange,
  placeholder = '# Add your workflow configuration here\n',
  isLoading = false,
}: WorkflowConfigEditorProps) {
  const validationErrorRef = useRef<string | null>(null);

  const validateAndNotify = (content: string) => {
    try {
      yaml.load(content);
      validationErrorRef.current = null;
      onChange(content, true);
    } catch (error: any) {
      validationErrorRef.current = error.message;
      onChange(content, false);
    }
  };

  // Notify parent whenever value changes from outside
  useEffect(() => {
    if (value) {
      validateAndNotify(value);
    }
  }, [value]);

  const highlightYaml = (code: string) =>
    Prism.highlight(code, Prism.languages.yaml, 'yaml');

  const validationError = (() => {
    try {
      yaml.load(value);
      return null;
    } catch (e: any) {
      return e.message as string;
    }
  })();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 bg-gray-900 rounded-lg border-2 border-gray-700">
        <div className="flex items-center gap-3 text-gray-400">
          <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span className="text-sm">Loading configuration...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {validationError && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-amber-800 dark:text-amber-200 text-sm">
          <strong>Invalid YAML:</strong> {validationError}
        </div>
      )}
      <div className="relative">
        <div className="bg-gray-900 rounded-lg overflow-hidden border-2 border-gray-700 focus-within:border-blue-500 transition-colors">
          <div className="max-h-[500px] overflow-auto scrollbar-always">
            <Editor
              value={value || placeholder}
              onValueChange={(newValue) => validateAndNotify(newValue)}
              highlight={highlightYaml}
              padding={16}
              style={{
                fontFamily: '"Fira Code", "Fira Mono", monospace',
                fontSize: 13,
                backgroundColor: '#1e293b',
                color: '#e2e8f0',
                minHeight: '280px',
              }}
              className="outline-none"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
